import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, ChevronDown, ChevronRight, Scale, CreditCard, ShieldCheck, UserX, Globe, Mail } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SEOMeta } from '@/components/SEOMeta';
import { PageBreadcrumb } from '@/components/PageBreadcrumb';
import { Link } from 'react-router-dom';

const BREADCRUMBS = [{ label: 'Правила и условия', href: '/terms' }];
const LAST_UPDATED = '27.06.2026';

const sections = [
  {
    id: 'parties',
    icon: Scale,
    title: '1. Страни по договора',
    content: `Настоящите Общи условия уреждат отношенията между:

• Доставчик: portion.bg (по-нататък „Платформата"), достъпна на адрес https://portion.bg, предоставяна от нейните оператори.

• Потребител: всяко физическо лице, което се регистрира или използва услугите на Платформата.

Използването на услугите на portion.bg означава, че сте прочели, разбрали и приели настоящите Общи условия. Ако не приемате тези условия, не използвайте Платформата.`,
  },
  {
    id: 'service',
    icon: Globe,
    title: '2. Описание на услугата',
    content: `portion.bg предоставя следните дигитални услуги:

• TDEE Калкулатор — изчисляване на дневен калориен прием въз основа на въведени от потребителя данни (възраст, тегло, ръст, пол, активност, цел).

• Генератор на хранителни планове — персонализирани 7-дневни хранителни планове, генерирани с помощта на изкуствен интелект.

• Списък за пазаруване — автоматично генериран списък с продукти в три ценови нива.

Всички услуги имат образователен и информационен характер. Те НЕ представляват медицинска консултация, диагноза или лечение. Препоръчваме консултация с лекар или диетолог преди промяна на хранителния режим.`,
  },
  {
    id: 'registration',
    icon: ShieldCheck,
    title: '3. Регистрация и акаунт',
    content: `3.1. За достъп до персонализирани услуги е необходима регистрация с валиден имейл адрес и парола.

3.2. Потребителят е длъжен да предоставя вярна, точна и актуална информация при регистрация.

3.3. Потребителят носи пълна отговорност за сигурността на своя акаунт и парола. Всяко действие, извършено от акаунта, се счита за извършено от потребителя.

3.4. При съмнение за неоторизиран достъп, потребителят е длъжен незабавно да уведоми Платформата на info@portion.bg.

3.5. Платформата си запазва правото да прекрати акаунт при установяване на нарушение на настоящите условия.`,
  },
  {
    id: 'credits',
    icon: CreditCard,
    title: '4. Кредитна система и плащания',
    content: `4.1. Кредити

Услугите на Платформата се заплащат чрез вътрешна кредитна система. Генерирането на нов хранителен план изразходва кредити от баланса на потребителя.

4.2. Закупуване на кредити

Кредитите се закупуват чрез защитена платформа за плащания (Stripe). Всички транзакции се обработват в евро (EUR). Платформата не съхранява данни за платежни карти.

4.3. Цени

Цените на кредитите и абонаментните планове са посочени на страница „Ценообразуване". Платформата си запазва правото да променя цените, като уведомява потребителите поне 7 дни предварително.

4.4. Политика за възстановяване

Тъй като кредитите са дигитален продукт и се предоставят незабавно след плащане, закупените кредити не подлежат на възстановяване. Това е в съответствие с Директива 2011/83/ЕС на ЕП за изключение на дигитален контент.

4.5. Абонамент

Месечният абонамент дава достъп до пълния 7-дневен план. Абонаментът се подновява автоматично, освен ако не бъде отменен преди датата на подновяване.`,
  },
  {
    id: 'obligations',
    icon: UserX,
    title: '5. Задължения и забранени действия',
    content: `Потребителят се задължава да не:

• Използва Платформата за незаконни цели или по начин, нарушаващ правата на трети лица.
• Осъществява неоторизиран достъп до системите на Платформата (хакерство, ботове, скрейпинг).
• Споделя акаунти с трети лица с цел заобикаляне на кредитната система.
• Разпространява, препродава или сублицензира съдържание от Платформата без изрично писмено разрешение.
• Въвежда невярна информация, създадена с цел манипулиране на системата.
• Извършва действия, нарушаващи функционирането или сигурността на Платформата.

При нарушение на тези забрани, Платформата си запазва правото незабавно да прекрати достъпа на потребителя без право на компенсация.`,
  },
  {
    id: 'liability',
    icon: Scale,
    title: '6. Ограничаване на отговорността',
    content: `6.1. Платформата предоставя услугите „такива, каквито са" (as-is) и не гарантира, че информацията е пълна, точна или актуална.

6.2. Платформата не носи отговорност за:
• Здравословни последствия от следване на генерираните хранителни планове.
• Пропуснати ползи или загуби, настъпили в резултат на ползването на Платформата.
• Прекъсвания в достъпа или технически неизправности.
• Сигурността на данните при форсмажорни обстоятелства.

6.3. Максималната отговорност на Платформата към потребителя е ограничена до размера на платените суми от потребителя за последните 30 дни.

6.4. Платформата не е медицинско заведение и не предоставя медицински услуги. Информацията е с образователна цел.`,
  },
  {
    id: 'ip',
    icon: FileText,
    title: '7. Интелектуална собственост',
    content: `7.1. Всички авторски права, търговски марки, дизайн, код и съдържание на Платформата са собственост на portion.bg или съответните им собственици.

7.2. Потребителят получава ограничено, непрехвърляемо право да използва Платформата единствено за лични, нетърговски цели.

7.3. Генерираните хранителни планове се предоставят за лична употреба на потребителя. Тяхното комерсиално разпространение или публикуване без разрешение е забранено.

7.4. Предоставянето на обратна връзка или предложения към Платформата се счита за безвъзмездно предоставени и могат да бъдат използвани без ограничения.`,
  },
  {
    id: 'privacy',
    icon: ShieldCheck,
    title: '8. Защита на личните данни',
    content: `Платформата обработва лични данни в съответствие с Регламент (ЕС) 2016/679 (GDPR) и Закона за защита на личните данни.

Пълната информация относно обработването на лични данни, правата Ви като субект на данни и политиката за бисквитки е налична в нашата Политика за поверителност.

За въпроси, свързани с личните Ви данни, можете да се свържете с нас на: info@portion.bg`,
  },
  {
    id: 'changes',
    icon: FileText,
    title: '9. Промени в условията',
    content: `9.1. Платформата си запазва правото да изменя настоящите Общи условия по всяко време.

9.2. При съществени промени потребителите ще бъдат уведомени чрез имейл или известие в Платформата поне 7 дни преди влизането им в сила.

9.3. Продължаването на използването на Платформата след влизането в сила на измененията се счита за приемане на новите условия.

9.4. Актуалната версия на Общите условия е достъпна на https://portion.bg/terms.`,
  },
  {
    id: 'law',
    icon: Scale,
    title: '10. Приложимо право и разрешаване на спорове',
    content: `10.1. Настоящите Общи условия се уреждат и тълкуват съгласно законодателството на Република България.

10.2. Всички спорове, възникнали от или по повод на тези условия, ще се решават по взаимно съгласие. При невъзможност за постигане на споразумение, споровете се отнасят до компетентния съд в Република България.

10.3. Потребителите — физически лица, имат право да подават жалби до Комисията за защита на потребителите (КЗП) на адрес www.kzp.bg.

10.4. Алтернативно разрешаване на спорове: https://ec.europa.eu/consumers/odr/`,
  },
  {
    id: 'contact',
    icon: Mail,
    title: '11. Контакти',
    content: `За въпроси, свързани с тези Общи условия, моля свържете се с нас:

• Имейл: info@portion.bg
• Уебсайт: https://portion.bg/contact
• Работно време: понеделник – петък, 09:00 – 18:00 ч.

Отговаряме в рамките на 2 работни дни.`,
  },
];

function Section({ section, index }) {
  const [open, setOpen] = useState(index < 2);
  const Icon = section.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04 }}
      className="border border-[#E6EDF5] rounded-xl overflow-hidden bg-white dark:bg-slate-800"
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 dark:bg-slate-900/70 transition-colors duration-150 group"
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${open ? 'bg-emerald-100' : 'bg-gray-100 dark:bg-slate-700 group-hover:bg-emerald-50'}`}>
            <Icon className={`w-4 h-4 transition-colors duration-200 ${open ? 'text-emerald-600' : 'text-gray-400'}`} />
          </div>
          <span className={`text-[14px] font-semibold transition-colors duration-200 ${open ? 'text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-200'}`}>
            {section.title}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-1 border-t border-gray-100 dark:border-slate-700">
              <div className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed whitespace-pre-line pl-11">
                {section.content}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function TermsPage() {
  return (
    <>
      <SEOMeta
        title="Общи условия"
        description="Общи условия и правила за ползване на portion.bg — регистрация, кредитна система, плащания, ограничаване на отговорността и приложимо право."
        keywords="portion.bg общи условия, правила ползване, кредити правила, поверителност GDPR"
        canonical="/terms"
        breadcrumbs={BREADCRUMBS}
      />
      <PageBreadcrumb items={BREADCRUMBS} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>

          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-14 h-14 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <FileText className="w-7 h-7 text-white" />
            </div>
            <Badge className="bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-600 border mb-4">Правни документи</Badge>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3 tracking-tight">Общи условия</h1>
            <p className="text-slate-400 text-sm">Последно обновени: {LAST_UPDATED}</p>
          </div>

          {/* Summary box */}
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-5 py-4 mb-8 flex items-start gap-3">
            <ChevronRight className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-emerald-800 leading-relaxed">
              Тези Общи условия уреждат използването на <strong>portion.bg</strong>. Моля прочетете ги внимателно преди регистрация.
              Като използвате Платформата, вие приемате настоящите условия в тяхната цялост.
            </p>
          </div>

          {/* Accordion sections */}
          <div className="space-y-2 mb-10">
            {sections.map((s, i) => (
              <Section key={s.id} section={s} index={i} />
            ))}
          </div>

          {/* Footer links */}
          <div className="bg-gray-50 dark:bg-slate-900 rounded-xl border border-[#E6EDF5] p-5 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500 dark:text-slate-400">
            <p>Свързани документи:</p>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="flex items-center gap-1.5 text-emerald-600 hover:text-emerald-700 font-medium transition-colors">
                <ShieldCheck className="w-3.5 h-3.5" /> Политика за поверителност
              </Link>
              <Link to="/disclaimer" className="flex items-center gap-1.5 text-amber-600 hover:text-amber-700 font-medium transition-colors">
                <Scale className="w-3.5 h-3.5" /> Медицински отказ
              </Link>
            </div>
          </div>

        </motion.div>
      </div>
    </>
  );
}
